import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { getEarlyAccessEnabled, updateEarlyAccessEnabled } from '../../../../features/settings/early-access';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { getProfile } from '../../../../features/profile';
import { Lx } from '../../../../i18n/consts';
import Card from '../card';
import { Box, Button } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import cardStyles from '../card/card.module.scss';
import TabLayout from '../../../../components/shared/tab-layout';
import SettingsHeader from '../settings-header';
import { canManageEarlyAccess } from '../../../../features/profile/entityPermissions';
import { NAVIGATION_SECTION_VISIBILITY } from '../../../../features/profile/navigationVisibility';
import { isSectionVisible } from '../../../../helpers/permissionHelper';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { useNavigate } from 'react-router-dom';

export default function EarlyAccessSetting() {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const { enabled } = useAppSelector(store => store.earlyAccess);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMob = useMediaQuery({ maxWidth: '1000px' });
    const canViewEarlyAccess = isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.settingsEarlyAccess);
    const canEditEarlyAccess = canManageEarlyAccess(profile);

    const handleToggle = useCallback(() => {
        if (!canEditEarlyAccess) {
            return;
        }

        setIsSubmitting(true);
        dispatch(updateEarlyAccessEnabled(!enabled))
            .unwrap()
            .then(() => {
                dispatch(getEarlyAccessEnabled());
            })
            .finally(() => setIsSubmitting(false));
    }, [canEditEarlyAccess, dispatch, enabled]);

    useEffect(() => {
        if (!canViewEarlyAccess) {
            navigate(NAVIGATION_ROUTES.SETTINGS, { replace: true });
            return;
        }

        setLoading(true);
        dispatch(getEarlyAccessEnabled())
            .finally(() => setLoading(false));
        dispatch(getProfile());
    }, [canViewEarlyAccess, dispatch, enabled, navigate]);

    if (!canViewEarlyAccess) {
        return null;
    }

    const statusText = enabled
        ? intl.formatMessage({ id: Lx.Settings.EARLY_ACCESS.ACTIVE })
        : intl.formatMessage({ id: Lx.Settings.EARLY_ACCESS.NOT_ENABLED });

    return (
        <TabLayout header={<SettingsHeader activeTab={'earlyAccess'} loading={loading || isSubmitting} />}>
            <Box sx={{ width: '100%', minWidth: 0, padding: isMob ? '12px' : '0 32px 16px' }}>
                <Card
                    title={intl.formatMessage({ id: Lx.Settings.EARLY_ACCESS.TITLE })}
                    statusText={statusText}
                    isStatusActive={enabled}
                    actions={
                        <Button
                            className={`${cardStyles.actionButton} ${enabled ? cardStyles.actionButtonActive : cardStyles.actionButtonInactive}`}
                            onClick={handleToggle}
                            disabled={isSubmitting || !canEditEarlyAccess}>
                            {enabled
                                ? intl.formatMessage({ id: Lx.Settings.EARLY_ACCESS.DISABLE })
                                : intl.formatMessage({ id: Lx.Settings.EARLY_ACCESS.ENABLE })}
                        </Button>
                    }>
                    <div style={{gap: '4px', display: 'flex', flexDirection: 'column'}}>
                        <div>{intl.formatMessage({id: Lx.Settings.EARLY_ACCESS.DESCRIPTION_PART1})}</div>
                        <div>{intl.formatMessage({id: Lx.Settings.EARLY_ACCESS.DESCRIPTION_PART2})}</div>
                        <div>{intl.formatMessage({id: Lx.Settings.EARLY_ACCESS.DESCRIPTION_PART3})}</div>
                    </div>
                </Card>
            </Box>
        </TabLayout>
    );
}
